import { useCallback } from "react";
import useSWR from "swr";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery, PaginationQueryWithFields } from "../types";
import { buildUrl, mutateCache, useApi } from "./api";
import { CustomError } from "./errors";
import { UserAppAccess } from "./types/accessControl";
import { UserUsage } from "./types/usageStatistics";
import { AddUserBody, AddUserResponse, UpdateUserBody, User } from "./types/users";

export const useUsersApi = () => {
  const api = useApi();

  const getAdminUsers = useCallback(
    (query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl("/admin/users", query);
      return useSWRImmutable<Paginated<User>, CustomError, string>(url, (url) =>
        api.get<Paginated<User>>({ url, signal }),
      );
    },
    [api],
  );

  const getAdminUser = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/users/${id}`;
      return useSWRMutation<User, CustomError, string>(url, (url) => api.get<User>({ url, signal }));
    },
    [api],
  );

  const getAdminUserAppAccesses = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/users/${id}/app_accesses`;
      return useSWR<UserAppAccess[], CustomError, string>(url, () => api.get<UserAppAccess[]>({ url, signal }));
    },
    [api],
  );

  const getAdminUserUsages = useCallback(
    (id: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/users/${id}/usages`, query);
      return useSWR<Paginated<UserUsage>, CustomError, string>(url, () =>
        api.get<Paginated<UserUsage>>({ url, signal }),
      );
    },
    [api],
  );

  const updateUser = useCallback(
    async (itemId: string, body: UpdateUserBody): Promise<void> => {
      const url = `/admin/users/${itemId}`;
      await api.put<UpdateUserBody>(url, body);
      mutateCache(["/admin/users"], url);
    },
    [api],
  );

  // The user stored right away, or pending for an email without an account yet: both lists are revalidated.
  const addUser = useCallback(
    async (body: AddUserBody): Promise<AddUserResponse> => {
      const result = await api.post<AddUserResponse, AddUserBody>("/admin/users", body);
      mutateCache(["/admin/users", "/admin/pending_users"]);
      return result;
    },
    [api],
  );

  const getAdminUsersWithFields = useCallback(
    async <K extends keyof User>(query: PaginationQueryWithFields<Pick<User, K>>, signal: AbortSignal) => {
      const url = buildUrl<Pick<User, K>>("/admin/users", query);
      const x = await api.get<Paginated<Pick<User, K>>>({ url, signal });
      return x.items;
    },
    [api],
  );

  return {
    getAdminUsersWithFields,
    getAdminUsers,
    getAdminUser,
    getAdminUserAppAccesses,
    getAdminUserUsages,
    updateUser,
    addUser,
  };
};
